import { useParams, useNavigate, Link } from 'react-router-dom'
import { useState } from 'react'
import { ArrowLeft, Sparkles, Database, FileText, Loader2, Download, Trash2, Wifi, WifiOff, RotateCcw, Play, Square } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import {
  useGenClairRunDetail,
  useGenClairRunResult,
  useGenClairRunGraph,
  useDeleteGenClairRun,
  useRetryGenClairRun,
  useCancelGenClairRun,
  exportGenClairRun,
} from '@/hooks/useGenClairRuns'
import { TaxonomyRun, TaxonomyIntent, GraphData } from '@/hooks/useTaxonomyRun'
import { useWebSocketTaxonomy } from '@/hooks/useWebSocketTaxonomy'
import { ConnectionState } from '@/lib/websocket-manager'
import { RunningState } from '@/components/knowledge/genclair/RunningState'
import { CompletedState } from '@/components/knowledge/genclair/CompletedState'
import { ErrorState } from '@/components/knowledge/genclair/ErrorState'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const STEP_LABELS: Record<string, string> = {
  fetch: 'Fetch Chunks',
  preprocess: 'Preprocess',
  embed: 'Embed',
  cluster: 'Cluster',
  refine: 'Refine Outliers',
  label: 'LLM Labeling',
  relations: 'Entity Relations',
}

export function GenClairDetail() {
  const { runId } = useParams<{ runId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const deleteRun = useDeleteGenClairRun()
  const retryRun = useRetryGenClairRun()
  const cancelRun = useCancelGenClairRun()

  // Initial fetch for run metadata (name, source_type, etc.)
  const { data: runDetail, isLoading: detailLoading } = useGenClairRunDetail(runId)

  // WebSocket for real-time status updates
  const wsChannelId = runDetail?.knowledge_base_id || runId || ''
  const { runData: wsRunData, connectionState: wsConnectionState } = useWebSocketTaxonomy(wsChannelId)

  // Result + graph only when completed
  const effectiveStatus = wsRunData?.status || runDetail?.status
  const isCompleted = effectiveStatus === 'completed'
  const { data: resultData } = useGenClairRunResult(runId, isCompleted)
  const { data: graphData } = useGenClairRunGraph(runId, isCompleted)

  const [isExporting, setIsExporting] = useState(false)

  if (detailLoading || !runDetail) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Build effective run state: prefer WebSocket data when available
  const adaptedRun: TaxonomyRun = wsRunData && wsConnectionState === ConnectionState.CONNECTED
    ? {
        run_id: wsRunData.run_id,
        status: wsRunData.status,
        progress: wsRunData.progress,
        total_chunks_processed: wsRunData.total_chunks_processed,
        intents_discovered: wsRunData.intents_discovered,
        error_log: wsRunData.error_log || '',
        created_at: wsRunData.created_at,
        completed_at: wsRunData.completed_at,
        step: wsRunData.step,
        chunks_in_step: wsRunData.chunks_in_step,
      }
    : {
        run_id: runDetail.id,
        status: runDetail.status,
        progress: runDetail.progress,
        total_chunks_processed: runDetail.total_chunks_processed,
        intents_discovered: runDetail.intents_discovered,
        error_log: runDetail.error_log,
        created_at: runDetail.created_at,
        completed_at: runDetail.completed_at,
      }

  const handleResume = async () => {
    try {
      await retryRun.mutateAsync({ runId: runDetail.id, mode: 'resume' })
      const step = runDetail.last_completed_step
      toast({ title: 'Resuming', description: step ? `Resuming from after "${STEP_LABELS[step] || step}"` : 'Pipeline re-enqueued.' })
    } catch (err: any) {
      toast({ title: 'Resume failed', description: err?.response?.data?.error || 'Something went wrong', variant: 'destructive' })
    }
  }

  const handleRestart = async () => {
    try {
      await retryRun.mutateAsync({ runId: runDetail.id, mode: 'restart' })
      toast({ title: 'Restarting', description: 'Pipeline restarting from scratch.' })
    } catch (err: any) {
      toast({ title: 'Restart failed', description: err?.response?.data?.error || 'Something went wrong', variant: 'destructive' })
    }
  }

  const handleCancel = async () => {
    try {
      await cancelRun.mutateAsync(runDetail.id)
      toast({ title: 'Cancelled', description: 'Run cancelled. You can resume or restart later.' })
    } catch (err: any) {
      toast({ title: 'Cancel failed', description: err?.response?.data?.error || 'Something went wrong', variant: 'destructive' })
    }
  }

  const handleDelete = async () => {
    try {
      await deleteRun.mutateAsync(runDetail.id)
      toast({ title: 'Run deleted', description: 'GenClair run and all related data deleted.' })
      navigate('/app/genclair')
    } catch (err: any) {
      toast({ title: 'Error', description: err?.response?.data?.error || 'Failed to delete', variant: 'destructive' })
    }
  }

  const handleExport = async (format: 'json' | 'csv') => {
    setIsExporting(true)
    try {
      await exportGenClairRun(runDetail.id, format)
      toast({ title: 'Exported', description: `Taxonomy exported as ${format.toUpperCase()}.` })
    } catch (err: any) {
      toast({ title: 'Export failed', description: err.message || 'Something went wrong', variant: 'destructive' })
    } finally {
      setIsExporting(false)
    }
  }

  const uiState: 'empty' | 'running' | 'completed' | 'error' | 'cancelled' = (() => {
    if (adaptedRun.status === 'completed') return 'completed'
    if (adaptedRun.status === 'failed') return 'error'
    if (adaptedRun.status === 'cancelled') return 'cancelled'
    if (adaptedRun.status === 'pending' || adaptedRun.status === 'running') return 'running'
    return 'empty'
  })()

  const canRetry = uiState === 'error' || uiState === 'cancelled'
  const canCancel = uiState === 'running'
  const hasCheckpoint = !!runDetail.last_completed_step

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-2">
            <Link to="/app/genclair">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to GenClair
            </Link>
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-purple-500" />
            {runDetail.name || 'Untitled Run'}
          </h1>
          <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
            {runDetail.source_type === 'ragflow_dataset' ? (
              <span className="flex items-center gap-1"><Database className="h-4 w-4 text-blue-500" />{runDetail.knowledge_base_name || 'Knowledge Base'}</span>
            ) : (
              <span className="flex items-center gap-1"><FileText className="h-4 w-4 text-amber-500" />{runDetail.source_file_name || 'File Upload'}</span>
            )}
            <StatusBadge status={adaptedRun.status} />
            {adaptedRun.progress > 0 && adaptedRun.status !== 'completed' && (
              <span>{adaptedRun.progress}%</span>
            )}
            {hasCheckpoint && canRetry && (
              <Badge variant="outline" className="text-xs">
                Checkpoint: {STEP_LABELS[runDetail.last_completed_step] || runDetail.last_completed_step}
              </Badge>
            )}
            {wsConnectionState === ConnectionState.CONNECTED ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <Wifi className="w-3 h-3 mr-1" /> Realtime
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-amber-50 text-amber-700">
                <WifiOff className="w-3 h-3 mr-1" /> Polling
              </Badge>
            )}
          </div>
          {runDetail.description && (
            <p className="text-muted-foreground mt-1 text-sm">{runDetail.description}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Resume button — only for failed/cancelled runs with a checkpoint */}
          {canRetry && hasCheckpoint && (
            <Button
              variant="default"
              size="sm"
              onClick={handleResume}
              disabled={retryRun.isPending}
            >
              {retryRun.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
              Resume
            </Button>
          )}

          {/* Restart button — for failed/cancelled runs */}
          {canRetry && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={retryRun.isPending}>
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Restart
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Restart from scratch?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will clear all checkpoint data and restart the entire pipeline from the beginning.
                    {hasCheckpoint && ' Your existing checkpoint will be lost.'}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep checkpoint</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRestart}>
                    Restart pipeline
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {/* Cancel button — only for running/pending runs */}
          {canCancel && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={cancelRun.isPending}
              className="text-amber-600 hover:text-amber-700"
            >
              {cancelRun.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Square className="h-4 w-4 mr-1" />}
              Cancel
            </Button>
          )}

          {isCompleted && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExporting}>
                  {isExporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleExport('json')}>Export as JSON</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('csv')}>Export as CSV</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this run?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete the run and all discovered taxonomy data (intents, tags, entities, graph).
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Main content */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Taxonomy Discovery</CardTitle>
        </CardHeader>
        <CardContent>
          {uiState === 'running' && (
            <>
              <RunningState run={adaptedRun} />
              {adaptedRun.step && wsConnectionState === ConnectionState.CONNECTED && (
                <div className="mt-2 text-center">
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    Current step: {STEP_LABELS[adaptedRun.step] || adaptedRun.step}
                    {adaptedRun.chunks_in_step !== undefined && adaptedRun.chunks_in_step > 0 && (
                      <span className="ml-1">({adaptedRun.chunks_in_step} chunks)</span>
                    )}
                  </Badge>
                </div>
              )}
            </>
          )}
          {uiState === 'completed' && (
            <CompletedState
              run={adaptedRun}
              intents={(resultData?.intents ?? []) as TaxonomyIntent[]}
              graphData={(graphData ?? { nodes: [], edges: [] }) as GraphData}
              kbId={runDetail.knowledge_base_id || runDetail.id}
              onReRun={() => retryRun.mutateAsync({ runId: runDetail.id, mode: 'restart' })}
              isReRunning={retryRun.isPending}
              onDelete={handleDelete}
              isDeleting={deleteRun.isPending}
            />
          )}
          {uiState === 'error' && <ErrorState run={adaptedRun} onRetry={handleResume} isRetrying={retryRun.isPending} />}
          {uiState === 'cancelled' && (
            <div className="text-center py-12">
              <Square className="h-8 w-8 mx-auto mb-3 text-amber-500" />
              <p className="text-lg font-medium mb-1">Run Cancelled</p>
              {hasCheckpoint && (
                <p className="text-sm text-muted-foreground mb-4">
                  Checkpoint saved at <strong>{STEP_LABELS[runDetail.last_completed_step] || runDetail.last_completed_step}</strong>.
                  You can resume from where it stopped or restart from scratch.
                </p>
              )}
              {!hasCheckpoint && (
                <p className="text-sm text-muted-foreground mb-4">
                  No checkpoint data available. Use Restart to run the pipeline again.
                </p>
              )}
              <div className="flex items-center justify-center gap-3">
                {hasCheckpoint && (
                  <Button onClick={handleResume} disabled={retryRun.isPending}>
                    {retryRun.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                    Resume
                  </Button>
                )}
                <Button variant="outline" onClick={handleRestart} disabled={retryRun.isPending}>
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Restart
                </Button>
              </div>
            </div>
          )}
          {uiState === 'empty' && (
            <div className="text-center py-12 text-muted-foreground">
              <Sparkles className="h-8 w-8 mx-auto mb-3" />
              <p>This run has not started yet.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'completed':
      return <Badge variant="default" className="bg-green-600">Completed</Badge>
    case 'running':
      return <Badge variant="secondary" className="animate-pulse">Running</Badge>
    case 'pending':
      return <Badge variant="outline">Pending</Badge>
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>
    case 'cancelled':
      return <Badge variant="secondary" className="bg-amber-100 text-amber-800">Cancelled</Badge>
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}
